import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { 
  Package, 
  Search, 
  X, 
  RotateCw, 
  AlertTriangle, 
  TrendingDown, 
  Boxes, 
  CheckCircle2, 
  XCircle, 
  Image as ImageIcon, 
  Eye, 
  Layers,
  Tag,
  ArrowUpDown
} from "lucide-react";
import { SmartNetworkImage } from "../../../components/SmartNetworkImage";
import { ImageLightbox } from "../../../components/ImageLightbox";
import { Category } from "../../../types/category";
import { Product } from "../../../types/product";
import api from "../../../service/api";

interface CategoryProductsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  category: Category | null;
}

type StockFilterType = "all" | "in_stock" | "critical" | "out_of_stock";

export const CategoryProductsDialog: React.FC<CategoryProductsDialogProps> = ({
  isOpen,
  onClose,
  category,
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [stockFilter, setStockFilter] = useState<StockFilterType>("all");
  const [sortBy, setSortBy] = useState<string>("name_asc");
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(15);

  // Görsel Galerisi (Lightbox) State'i
  const [galleryState, setGalleryState] = useState<{
    isOpen: boolean;
    images: string[];
    currentIndex: number;
    title?: string;
    sku?: string;
  }>({
    isOpen: false,
    images: [],
    currentIndex: 0,
  });

  // Arama için debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPageIndex(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Dialog açıldığında veya kategori değiştiğinde state'leri sıfırla
  useEffect(() => {
    if (isOpen) {
      setSearch("");
      setDebouncedSearch("");
      setStockFilter("all");
      setSortBy("name_asc");
      setPageIndex(1);
    } else {
      setProducts([]);
      setTotal(0);
    }
  }, [isOpen, category?.id]);

  // Ürünleri Yükleme Fonksiyonu
  const loadProducts = useCallback(async () => {
    if (!category?.id) return;
    setLoading(true);

    try {
      const filters: any[] = [
        { field: "categoryId", operator: "eq", value: category.id }
      ];

      // Metin araması
      if (debouncedSearch.trim()) {
        filters.push({
          logic: "or",
          filters: [
            { field: "name", operator: "contains", value: debouncedSearch.trim() },
            { field: "sku", operator: "contains", value: debouncedSearch.trim() },
            { field: "barcode", operator: "contains", value: debouncedSearch.trim() },
          ]
        });
      }

      // Stok filtreleri
      if (stockFilter === "in_stock") {
        filters.push({ field: "quantity", operator: "gt", value: 0 });
      } else if (stockFilter === "out_of_stock") {
        filters.push({ field: "quantity", operator: "lte", value: 0 });
      }

      // Sıralama belirleme
      let sort: { field: string; dir: "asc" | "desc" }[] = [{ field: "name", dir: "asc" }];
      if (sortBy === "name_desc") sort = [{ field: "name", dir: "desc" }];
      else if (sortBy === "quantity_desc") sort = [{ field: "quantity", dir: "desc" }];
      else if (sortBy === "quantity_asc") sort = [{ field: "quantity", dir: "asc" }];
      else if (sortBy === "created_desc") sort = [{ field: "createdAt", dir: "desc" }];

      const response = await api.post("/stock/DynamicQuery", {
        pageIndex,
        pageSize,
        filter: {
          logic: "and",
          filters,
        },
        sort,
      });

      const data = response.data || response;
      let fetchedItems: Product[] = data.items || [];

      // Kritik stok filtresi istemci tarafında da desteklenir
      if (stockFilter === "critical") {
        fetchedItems = fetchedItems.filter(
          (p) => p.quantity > 0 && p.quantity <= (p.criticalStock || 0)
        );
      }

      setProducts(fetchedItems);
      setTotal(data.total || 0);
    } catch (error) {
      console.error("Kategori ürünleri yüklenirken hata oluştu:", error);
    } finally {
      setLoading(false);
    }
  }, [category?.id, debouncedSearch, stockFilter, sortBy, pageIndex, pageSize]);

  useEffect(() => {
    if (isOpen && category?.id) {
      loadProducts();
    }
  }, [isOpen, loadProducts]);

  // Fiyat Formatlayıcı
  const formatCurrency = (value: number | undefined, currency: string = "TRY") => {
    if (value === undefined || value === null) return "---";
    const validCurrency = currency === "TL" ? "TRY" : currency;
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: validCurrency,
    }).format(value);
  };

  // Sütun Şablonları
  const imageBodyTemplate = (row: Product) => {
    const imageUrls = (row.images || [])
      .filter((img: any) => img?.url && typeof img.url === "string" && img.url.trim().length > 0)
      .map((img: any) => `${img.url}`);
    const hasImages = imageUrls.length > 0;

    return (
      <div
        onClick={() => {
          if (hasImages) {
            setGalleryState({
              isOpen: true,
              images: imageUrls,
              currentIndex: 0,
              title: row.name,
              sku: row.barcode || row.sku,
            });
          }
        }}
        className={`relative group w-11 h-11 rounded-xl overflow-hidden flex items-center justify-center border border-borderColor bg-card shadow-2xs transition-all duration-200 ${
          hasImages
            ? "cursor-pointer hover:border-primary hover:shadow-md hover:scale-105"
            : "opacity-40"
        }`}
        title={hasImages ? "Görselleri büyütmek için tıklayın" : "Görsel yok"}
      >
        <SmartNetworkImage
          imageUrls={imageUrls}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
        />
        {hasImages && (
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white backdrop-blur-[1px]">
            <Eye size={14} className="transform group-hover:scale-110 transition-transform drop-shadow" />
          </div>
        )}
        {imageUrls.length > 1 && (
          <div className="absolute bottom-0.5 right-0.5 px-1 py-0.2 rounded bg-black/75 text-white text-[8px] font-black tracking-tighter flex items-center gap-0.5">
            <ImageIcon size={8} /> {imageUrls.length}
          </div>
        )}
      </div>
    );
  };

  const codeBodyTemplate = (row: Product) => (
    <div className="flex flex-col gap-0.5">
      <span className="font-mono text-xs text-blue-600 dark:text-blue-400 font-bold bg-blue-50 dark:bg-blue-950/50 px-2 py-0.5 rounded-md border border-blue-100 dark:border-blue-900/60 w-fit">
        {row.barcode || row.sku || "---"}
      </span>
      {row.sku && row.barcode && row.sku !== row.barcode && (
        <span className="font-mono text-[10px] text-gray-500 tracking-tight">
          SKU: {row.sku}
        </span>
      )}
    </div>
  );

  const nameBodyTemplate = (row: Product) => {
    const hasVariants = row.variants && row.variants.length > 0;
    return (
      <div className="flex flex-col gap-1">
        <span className="font-semibold text-sm text-text line-clamp-2" title={row.name}>
          {row.name}
        </span>
        <div className="flex items-center gap-2 flex-wrap">
          {hasVariants && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800/60 px-1.5 py-0.2 rounded-md">
              <Layers size={10} /> {row.variants?.length} Varyant
            </span>
          )}
          {row.brand && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-1.5 py-0.2 rounded-md">
              <Tag size={10} /> {row.brand.name}
            </span>
          )}
        </div>
      </div>
    );
  };

  const quantityBodyTemplate = (row: Product) => {
    const isOutOfStock = row.quantity <= 0;
    const isCritical = !isOutOfStock && row.quantity <= (row.criticalStock || 0);

    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1.5">
          <span
            className={`font-bold text-sm ${
              isOutOfStock
                ? "text-red-600 dark:text-red-400"
                : isCritical
                ? "text-amber-600 dark:text-amber-400"
                : "text-emerald-600 dark:text-emerald-400"
            }`}
          >
            {row.quantity} {row.unit?.code || row.unit?.name || "Adet"}
          </span>
          {isCritical && (
            <span title={`Kritik Stok Seviyesi: ${row.criticalStock}`}>
              <AlertTriangle size={13} className="text-amber-500 animate-bounce-slow" />
            </span>
          )}
        </div>

        {isOutOfStock ? (
          <span className="text-[10px] font-bold text-red-500 uppercase tracking-tighter flex items-center gap-0.5">
            <XCircle size={10} /> Tükendi
          </span>
        ) : isCritical ? (
          <span className="text-[10px] font-bold text-amber-500 uppercase tracking-tighter flex items-center gap-0.5">
            <TrendingDown size={10} /> Kritik Stok
          </span>
        ) : (
          <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-tighter flex items-center gap-0.5">
            <CheckCircle2 size={10} /> Stokta Var
          </span>
        )}
      </div>
    );
  };

  const priceBodyTemplate = (row: Product) => {
    const primaryPrice = row.prices && row.prices.length > 0 ? row.prices[0].price : undefined;
    return (
      <div className="flex flex-col items-end">
        <span className="font-bold text-sm text-text">
          {formatCurrency(primaryPrice, row.purchaseCurrency || "TRY")}
        </span>
        {row.taxRate !== undefined && (
          <span className="text-[10px] text-gray-500 font-medium">
            +% {row.taxRate} KDV {row.taxIncluded ? "(Dahil)" : "(Hariç)"}
          </span>
        )}
      </div>
    );
  };

  // Header Elementi
  const headerElement = (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 w-full pr-6 pb-2 border-b border-borderColor">
      <div className="flex items-center gap-3">
        <div className="p-2.5 bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 rounded-2xl border border-emerald-500/20 shadow-xs">
          <Boxes size={22} />
        </div>
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-bold text-text">
              {category?.name || "Kategori"}
            </h2>
            <span className="px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 font-bold text-xs border border-emerald-200 dark:border-emerald-800">
              {total} Ürün
            </span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mt-0.5 flex items-center gap-1">
            {category?.parent ? (
              <>
                <span className="text-gray-400">{category.parent.name}</span>
                <span>›</span>
                <span className="text-gray-700 dark:text-gray-300 font-semibold">{category.name}</span>
              </>
            ) : (
              <span>Ana Kategoriye Bağlı Ürünler</span>
            )}
          </p>
        </div>
      </div>

      {/* Aksiyonlar */}
      <div className="flex items-center gap-2">
        <button
          onClick={loadProducts}
          disabled={loading}
          className="p-2 text-gray-600 dark:text-gray-300 hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition flex items-center gap-1.5 text-xs font-semibold border border-borderColor shadow-2xs"
          title="Yenile"
        >
          <RotateCw size={14} className={loading ? "animate-spin text-primary" : ""} />
          <span className="hidden sm:inline">Yenile</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      <Dialog
        header={headerElement}
        visible={isOpen}
        onHide={onClose}
        maximizable
        className="w-[95vw] lg:w-[85vw] xl:w-[75vw] max-w-6xl"
        contentClassName="p-4 bg-bg text-text"
        headerClassName="bg-card text-text border-b-0 p-4"
        resizable={false}
      >
        <div className="flex flex-col gap-4">
          {/* Arama ve Filtreleme Toolbar */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-3 bg-card border border-borderColor rounded-2xl shadow-2xs">
            {/* Arama Kutusu */}
            <div className="relative flex-1 max-w-md">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Ürün adı, barkod veya SKU ara..."
                className="w-full pl-9 pr-8 py-2 text-xs bg-bgAlt border border-borderColor rounded-xl text-text placeholder-gray-400 focus:outline-hidden focus:border-primary focus:ring-1 focus:ring-primary transition"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Stok Filtre Butonları */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0">
              <button
                onClick={() => { setStockFilter("all"); setPageIndex(1); }}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition border ${
                  stockFilter === "all"
                    ? "bg-primary text-white border-primary shadow-xs"
                    : "bg-bgAlt text-gray-600 dark:text-gray-300 border-borderColor hover:bg-gray-100 dark:hover:bg-gray-800"
                }`}
              >
                Tümü ({total})
              </button>
              <button
                onClick={() => { setStockFilter("in_stock"); setPageIndex(1); }}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition border ${
                  stockFilter === "in_stock"
                    ? "bg-emerald-600 text-white border-emerald-600 shadow-xs"
                    : "bg-bgAlt text-emerald-700 dark:text-emerald-400 border-borderColor hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                }`}
              >
                Stokta Var
              </button>
              <button
                onClick={() => { setStockFilter("critical"); setPageIndex(1); }}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition border ${
                  stockFilter === "critical"
                    ? "bg-amber-600 text-white border-amber-600 shadow-xs"
                    : "bg-bgAlt text-amber-700 dark:text-amber-400 border-borderColor hover:bg-amber-50 dark:hover:bg-amber-950/30"
                }`}
              >
                Kritik
              </button>
              <button
                onClick={() => { setStockFilter("out_of_stock"); setPageIndex(1); }}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition border ${
                  stockFilter === "out_of_stock"
                    ? "bg-red-600 text-white border-red-600 shadow-xs"
                    : "bg-bgAlt text-red-700 dark:text-red-400 border-borderColor hover:bg-red-50 dark:hover:bg-red-950/30"
                }`}
              >
                Tükenenler
              </button>
            </div>

            {/* Sıralama */}
            <div className="flex items-center gap-2">
              <ArrowUpDown size={14} className="text-gray-400 shrink-0" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-bgAlt border border-borderColor rounded-xl px-2.5 py-1.5 text-xs text-text focus:outline-hidden focus:border-primary font-medium"
              >
                <option value="name_asc">İsim (A → Z)</option>
                <option value="name_desc">İsim (Z → A)</option>
                <option value="quantity_desc">Stok (Çoktan Aza)</option>
                <option value="quantity_asc">Stok (Azdan Çoka)</option>
                <option value="created_desc">En Yeni Eklenenler</option>
              </select>
            </div>
          </div>

          {/* Ürün Tablosu */}
          <div className="border border-borderColor rounded-2xl overflow-hidden bg-card shadow-2xs">
            <DataTable
              value={products}
              loading={loading}
              emptyMessage={
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-gray-800/60 flex items-center justify-center text-gray-400 mb-3">
                    <Package size={28} />
                  </div>
                  <h3 className="text-sm font-bold text-text">
                    {debouncedSearch
                      ? "Aramanızla eşleşen ürün bulunamadı"
                      : "Bu kategoriye henüz ürün eklenmemiş"}
                  </h3>
                  <p className="text-xs text-gray-500 max-w-sm mt-1">
                    {debouncedSearch
                      ? "Farklı bir arama terimi deneyebilir veya filtreleri temizleyebilirsiniz."
                      : "Ürünler sayfasından bu kategoriye yeni ürünler atayabilirsiniz."}
                  </p>
                </div>
              }
              rowHover
              className="p-datatable-sm"
              responsiveLayout="scroll"
            >
              <Column
                header="Görsel"
                body={imageBodyTemplate}
                style={{ width: "65px", textAlign: "center" }}
              />
              <Column
                field="barcode"
                header="Barkod / SKU"
                body={codeBodyTemplate}
                style={{ width: "160px" }}
              />
              <Column
                field="name"
                header="Ürün Adı & Bilgileri"
                body={nameBodyTemplate}
              />
              <Column
                field="quantity"
                header="Stok Durumu"
                body={quantityBodyTemplate}
                style={{ width: "150px" }}
              />
              <Column
                field="price"
                header="Fiyat"
                body={priceBodyTemplate}
                style={{ width: "140px", textAlign: "right" }}
              />
            </DataTable>

            {/* Pagination & Alt Bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-t border-borderColor bg-bgAlt/50 text-xs text-gray-600 dark:text-gray-300">
              <div className="font-medium">
                Toplam <span className="font-bold text-text">{total}</span> üründen{" "}
                <span className="font-bold text-text">
                  {products.length > 0 ? (pageIndex - 1) * pageSize + 1 : 0}
                </span>
                -
                <span className="font-bold text-text">
                  {Math.min(pageIndex * pageSize, total)}
                </span>{" "}
                arası gösteriliyor
              </div>

              <div className="flex items-center gap-3 self-end sm:self-auto">
                <div className="flex items-center gap-1.5">
                  <span className="text-gray-500">Sayfa Başı:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPageIndex(1);
                    }}
                    className="bg-bg border border-borderColor rounded-lg px-2 py-1 text-xs text-text font-semibold"
                  >
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPageIndex((p) => Math.max(1, p - 1))}
                    disabled={pageIndex <= 1 || loading}
                    className="px-2.5 py-1 rounded-lg border border-borderColor bg-bg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed font-medium transition"
                  >
                    Önceki
                  </button>
                  <span className="px-2 font-bold text-text">
                    {pageIndex} / {Math.max(1, Math.ceil(total / pageSize))}
                  </span>
                  <button
                    onClick={() => setPageIndex((p) => p + 1)}
                    disabled={pageIndex >= Math.ceil(total / pageSize) || loading}
                    className="px-2.5 py-1 rounded-lg border border-borderColor bg-bg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed font-medium transition"
                  >
                    Sonraki
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Görsel Büyütme Lightbox */}
      <ImageLightbox
        isOpen={galleryState.isOpen}
        images={galleryState.images}
        currentIndex={galleryState.currentIndex}
        onIndexChange={(index) =>
          setGalleryState((prev) => ({ ...prev, currentIndex: index }))
        }
        onClose={() => setGalleryState((prev) => ({ ...prev, isOpen: false }))}
        title={galleryState.title}
        sku={galleryState.sku}
      />
    </>
  );
};
